var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { useCallback, useEffect, useMemo, useState } from 'react';
import { translate, useToastService } from '@lucidlink/uicore';
import { NOTIFICATION_EVENT_ACTIONS, useCopyToClipboardWithToast, useReporting, } from '@lucidlink/workspacemanagement';
import { useFsEntryDirectLink } from '#query-hooks';
import { buildNotificationEventUrl } from '#services';
const PROGRESS_OPEN_IN_DESKTOP_TOAST_ID = 'progress-open-in-desktop';
const PROGRESS_GENERATE_LINK_TOAST_ID = 'progress-open-in-desktop';
export const useFsEntryActions = ({ filespaceId, fsEntryKey, onErrorGenerateDirectLink: onErrorGenerateDirectLinkExternal, onErrorOpenInDesktop: onErrorOpenInDesktopExternal, onSuccessCopyLinkToClipboard, onSuccessGenerateDirectLink: onSuccessGenerateDirectLinkExternal, onSuccessOpenInDesktop: onSuccessOpenInDesktopExternal, workspaceId, }) => {
    _react_refresh_temp_1();
    const { addToast, removeToast } = useToastService();
    const { copyToClipboard } = useCopyToClipboardWithToast();
    const [isPendingForCopy, setIsPendingForCopy] = useState(false);
    const [isPendingForOpen, setIsPendingForOpen] = useState(false);
    const { reportEvent } = useReporting();
    const { query: { data: generatedLink, isError: isErrorDirectLink, isPending: isPendingDirectLink, }, } = useFsEntryDirectLink({
        filespaceId,
        fsEntryKey: fsEntryKey,
        workspaceId,
    });
    const onErrorGenerateDirectLink = useCallback(() => {
        removeToast(PROGRESS_GENERATE_LINK_TOAST_ID);
        if (onErrorGenerateDirectLinkExternal) {
            onErrorGenerateDirectLinkExternal();
        }
        addToast({
            content: {
                message: translate('Direct link was not generated successfully.'),
            },
            kind: 'negative',
            hasCloseButton: true,
            'data-testid': 'error-generate-direct-link',
        });
    }, [addToast, onErrorGenerateDirectLinkExternal, removeToast]);
    const onSuccessGenerateDirectLink = useCallback(() => {
        if (onSuccessGenerateDirectLinkExternal) {
            onSuccessGenerateDirectLinkExternal();
        }
        addToast({
            content: {
                message: translate('Link was copied to clipboard.'),
            },
            kind: 'positive',
            hasCloseButton: true,
            'data-testid': 'success-copy-direct-link',
        });
    }, [addToast, onSuccessGenerateDirectLinkExternal]);
    const onErrorOpenInDesktop = useCallback(() => {
        removeToast(PROGRESS_OPEN_IN_DESKTOP_TOAST_ID);
        // Passed from outside
        if (onErrorOpenInDesktopExternal) {
            onErrorOpenInDesktopExternal();
        }
        addToast({
            content: {
                message: translate('Not able to open the item in the desktop application.'),
            },
            kind: 'negative',
            hasCloseButton: true,
            'data-testid': 'error-open-in-desktop',
        });
    }, [addToast, onErrorOpenInDesktopExternal, removeToast]);
    const copyLink = useCallback(() => {
        reportEvent({
            name: 'Copy Link Clicked',
            'Filespace ID': filespaceId,
            'Workspace ID': workspaceId,
        });
        if (isErrorDirectLink) {
            onErrorGenerateDirectLink();
            return;
        }
        if (isPendingDirectLink) {
            addToast({
                content: {
                    message: translate('Generating link…'),
                },
                id: PROGRESS_GENERATE_LINK_TOAST_ID,
                kind: 'progressive',
                hasCloseButton: true,
                'data-testid': PROGRESS_GENERATE_LINK_TOAST_ID,
            });
            setIsPendingForCopy(true);
            return;
        }
        if (generatedLink) {
            void copyToClipboard(generatedLink, {
                onSuccess: onSuccessGenerateDirectLink,
            });
            if (onSuccessCopyLinkToClipboard) {
                onSuccessCopyLinkToClipboard();
            }
        }
    }, [
        addToast,
        copyToClipboard,
        filespaceId,
        generatedLink,
        isErrorDirectLink,
        isPendingDirectLink,
        onErrorGenerateDirectLink,
        onSuccessCopyLinkToClipboard,
        onSuccessGenerateDirectLink,
        reportEvent,
        workspaceId,
    ]);
    const onSuccessOpenInDesktop = useCallback((data) => {
        const linkData = data.split('/l/')[1];
        if (!linkData) {
            onErrorOpenInDesktop();
            return;
        }
        const linkDataSegments = linkData.split('/');
        const linkFormatVersion = linkDataSegments[0];
        const encodedFileData = linkDataSegments[1];
        const encodedEncryptedFileName = linkDataSegments[2];
        if (!linkFormatVersion ||
            !encodedFileData ||
            !encodedEncryptedFileName) {
            onErrorOpenInDesktop();
            return;
        }
        removeToast(PROGRESS_OPEN_IN_DESKTOP_TOAST_ID);
        const desktopEventWindowLocation = buildNotificationEventUrl(NOTIFICATION_EVENT_ACTIONS.DirectLink, {
            linkFormatVersion,
            encodedEncryptedFileName,
            encodedFileData,
        });
        if (onSuccessOpenInDesktopExternal) {
            onSuccessOpenInDesktopExternal();
        }
        window.location.href = desktopEventWindowLocation;
    }, [onErrorOpenInDesktop, onSuccessOpenInDesktopExternal, removeToast]);
    const openInDesktop = useCallback(() => {
        reportEvent({
            name: 'Open in File Explorer Clicked',
            'Filespace ID': filespaceId,
            'Workspace ID': workspaceId,
        });
        if (isErrorDirectLink) {
            onErrorOpenInDesktop();
            return;
        }
        if (isPendingDirectLink) {
            addToast({
                content: {
                    message: translate('Opening in desktop.'),
                },
                id: PROGRESS_OPEN_IN_DESKTOP_TOAST_ID,
                kind: 'progressive',
                hasCloseButton: true,
                'data-testid': PROGRESS_OPEN_IN_DESKTOP_TOAST_ID,
            });
            setIsPendingForOpen(true);
            return;
        }
        if (generatedLink) {
            onSuccessOpenInDesktop(generatedLink);
        }
    }, [
        addToast,
        filespaceId,
        generatedLink,
        isErrorDirectLink,
        isPendingDirectLink,
        onErrorOpenInDesktop,
        onSuccessOpenInDesktop,
        reportEvent,
        workspaceId,
    ]);
    useEffect(() => {
        if (isPendingForCopy && generatedLink) {
            setIsPendingForCopy(false);
            removeToast(PROGRESS_GENERATE_LINK_TOAST_ID);
            void copyToClipboard(generatedLink, {
                onSuccess: onSuccessGenerateDirectLink,
            });
        }
    }, [
        copyToClipboard,
        generatedLink,
        isPendingForCopy,
        onSuccessGenerateDirectLink,
        removeToast,
    ]);
    useEffect(() => {
        if (isPendingForOpen && generatedLink) {
            setIsPendingForOpen(false);
            onSuccessOpenInDesktop(generatedLink);
            removeToast(PROGRESS_OPEN_IN_DESKTOP_TOAST_ID);
        }
    }, [generatedLink, isPendingForOpen, onSuccessOpenInDesktop, removeToast]);
    const returnValue = useMemo(() => {
        return {
            copyLink,
            isPendingForCopy,
            isPendingForOpen,
            openInDesktop,
        };
    }, [copyLink, isPendingForCopy, isPendingForOpen, openInDesktop]);
    return returnValue;
};
_react_refresh_temp_1(useFsEntryActions, "sCfD8SRePXUMiwRmasxL+lAcmhA=", false, () => [useToastService, useCopyToClipboardWithToast, useReporting, useFsEntryDirectLink]);
